import { BaseUserService } from './baseService';
import {
  Role,
  UserWithRole,
  UsersResponse,
  RoleHistory,
} from './types';

export class UserCoreService extends BaseUserService {
  /**
   * Récupère la liste des utilisateurs avec leurs rôles
   */
  async getUsers(params?: {
    search?: string;
    role?: number;
    page?: number;
    limit?: number;
  }): Promise<UsersResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append('search', params.search);
      if (params?.role) queryParams.append('role', params.role.toString());
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const url = `${this.getApiBase()}/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Récupère les détails d'un utilisateur spécifique
   */
  async getUserById(id: number): Promise<{ success: boolean; data: UserWithRole; error?: string }> {
    try {
      const url = `${this.getApiBase()}/users/${id}`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Récupère la liste de tous les rôles disponibles
   */
  async getRoles(): Promise<{ success: boolean; data: Role[]; error?: string }> {
    try {
      const url = `${this.getApiBase()}/users/roles`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Met à jour le rôle d'un utilisateur
   */
  async updateUserRole(
    userId: number,
    rolesId: number,
    reason?: string
  ): Promise<{ success: boolean; message: string; data: UserWithRole; error?: string }> {
    try {
      const url = `${this.getApiBase()}/users/${userId}/role`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ roles_id: rolesId, reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Active ou désactive l'accès d'un utilisateur
   */
  async updateUserAccess(
    userId: number,
    activated: boolean,
    reason?: string
  ): Promise<{ success: boolean; message: string; data: UserWithRole; error?: string }> {
    try {
      const url = `${this.getApiBase()}/users/${userId}/access`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ activated, reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Récupère l'historique des changements de rôle d'un utilisateur
   */
  async getUserRoleHistory(userId: number): Promise<{
    success: boolean;
    data: RoleHistory[];
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/users/${userId}/role-history`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Envoie un email de réinitialisation de mot de passe à un utilisateur
   */
  async resetUserPassword(userId: number): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/users/${userId}/reset-password`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }
}
